<script setup>
// NOOVI: filtro global de período (pedido no mockup do redesenho da
// Overview) — reaproveita OverviewReportFilters.vue, já usado (e testado)
// por SummaryReports.vue nas outras telas de relatório (/reports/
// agents_overview etc.), em vez de construir um seletor novo. `provide` leva
// o filtro reativo pras seções que são de PERÍODO (Desempenho, Volume,
// Canais, Robô, SLA, CSAT, e as novas colunas de tempo de Agentes/Times) —
// as seções "Em tempo real" (Conversas Abertas, Status do agente, open/
// unattended das tabelas, heatmaps com paginação própria) ficam de fora de
// propósito, o período não faz sentido pra elas.
// Filtro de TIME (visto no mockup) ficou de fora deste ciclo:
// OverviewReportFilters não suporta team_id, e a "Todos Os Times" que já
// existe é local do card Conversas Abertas — adicionar um filtro de time
// global exigiria plumbing novo em todas as seções, escopo maior que o
// resto desta fatia.
import { computed, provide, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportHeader from './components/ReportHeader.vue';
import OverviewReportFilters from './components/OverviewReportFilters.vue';
import HeatmapTabsContainer from './components/heatmaps/HeatmapTabsContainer.vue';
import AgentLiveReportContainer from './components/AgentLiveReportContainer.vue';
import TeamLiveReportContainer from './components/TeamLiveReportContainer.vue';
import StatsLiveReportsContainer from './components/StatsLiveReportsContainer.vue';
import PerformanceSummaryContainer from './components/PerformanceSummaryContainer.vue';
import VolumeTrendContainer from './components/VolumeTrendContainer.vue';
// NOOVI: resumo de KPIs do Pipeline Pro na Overview (Gap D — fase-10).
import PipelineSummaryContainer from './components/PipelineSummaryContainer.vue';
import BotSummaryContainer from './components/BotSummaryContainer.vue';
import SlaSummaryContainer from './components/SlaSummaryContainer.vue';
import CsatSummaryContainer from './components/CsatSummaryContainer.vue';
import ChannelSummaryContainer from './components/ChannelSummaryContainer.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  OVERVIEW_DATE_FILTER_KEY,
  createDefaultDateFilter,
} from './helpers/overviewDateFilter';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { captainEnabled } = useCaptain();
const { isAdmin } = useAdmin();

const dateFilter = reactive(createDefaultDateFilter());
provide(OVERVIEW_DATE_FILTER_KEY, dateFilter);

const onFilterChange = ({ from, to, businessHours }) => {
  dateFilter.from = from;
  dateFilter.to = to;
  dateFilter.businessHours = businessHours;
};

const formattedRange = computed(
  () =>
    `${format(fromUnixTime(dateFilter.from), 'dd MMM')} – ${format(fromUnixTime(dateFilter.to), 'dd MMM')}`
);
</script>

<template>
  <ReportHeader
    :header-title="$t('OVERVIEW_REPORTS.HEADER')"
    :header-description="
      t('OVERVIEW_REPORTS.SHOWING_DATA_FOR', { range: formattedRange })
    "
  />
  <OverviewReportFilters class="mb-4" @filter-change="onFilterChange" />
  <div class="flex flex-col gap-4 pb-6">
    <StatsLiveReportsContainer />
    <PerformanceSummaryContainer />
    <VolumeTrendContainer />
    <ChannelSummaryContainer />
    <PipelineSummaryContainer v-if="isCloudFeatureEnabled('pipeline_board')" />
    <BotSummaryContainer v-if="captainEnabled" />
    <SlaSummaryContainer v-if="isAdmin" />
    <CsatSummaryContainer />
    <HeatmapTabsContainer />
    <AgentLiveReportContainer />
    <TeamLiveReportContainer />
  </div>
</template>
